import { useDispatch } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import {
    Typography,
    Button,
    Stack,
    TextField,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import { addCompaniesSearchFilters } from "reducers/company/companiesSearchFilters";
import { GlobalAppDataStorageService } from "infra/storage";
import { SUBSCRIPTION_STATUS_OPTIONS } from "utils/subscriptionStatus";

const sourceOptions = [
    { label: "Manual", value: "manual" },
    { label: "Stripe", value: "stripe" },
];

export const CompaniesFilters = () => {
    const { control, handleSubmit, reset } = useForm();
    const dispatch = useDispatch();

    const onSubmit = (data) => {
        if (!data?.renewable) delete data.renewable;
        dispatch(addCompaniesSearchFilters(data));
    };

    // Get all plans
    const subscriptionPlans =
        GlobalAppDataStorageService.getSubscriptionPlans();

    // Derive actual packages from plans
    const subscriptionPackagesOptions = subscriptionPlans.flatMap((plan) =>
        plan.packages.map((pkg) => {
            return {
                id: pkg.id,
                name: pkg.name,
            };
        }),
    );

    const handleDynamicSubmitForm = (event) => {
        event?.preventDefault?.();

        handleSubmit(onSubmit)();
    };

    return (
        <form onSubmit={handleDynamicSubmitForm}>
            <Stack direction="column" px={3} py={3} spacing={2}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Typography variant="header1" component="h3">
                        Filters
                    </Typography>
                    <Button
                        sx={{ textTransform: "none", padding: 0 }}
                        variant="text"
                        onClick={(event) => {
                            reset();
                            handleDynamicSubmitForm(event);
                        }}
                    >
                        Clear all
                    </Button>
                    <Button sx={{ display: "none" }} type="submit" />
                </Stack>

                <Stack spacing={3}>
                    {/* Company ID */}
                    <Controller
                        name="companyId"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Company ID"
                                variant="standard"
                                onBlur={handleDynamicSubmitForm}
                            />
                        )}
                    />

                    {/* Company Name */}
                    <Controller
                        name="companyName"
                        defaultValue=""
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                label="Company name"
                                variant="standard"
                                onBlur={handleDynamicSubmitForm}
                            />
                        )}
                    />

                    {/* Subscription Autocomplete */}
                    <Controller
                        name="packageId"
                        control={control}
                        defaultValue={null}
                        multiple={false}
                        render={({ field }) => (
                            <FormControl variant="standard" fullWidth>
                                <InputLabel>Subscription package</InputLabel>
                                <Select
                                    {...field}
                                    value={field.value ?? ""}
                                    onChange={(e) => {
                                        field.onChange(e.target.value);
                                        handleDynamicSubmitForm(e);
                                    }}
                                >
                                    <MenuItem value="">No selection</MenuItem>
                                    {subscriptionPackagesOptions.map((opt) => (
                                        <MenuItem key={opt.id} value={opt.id}>
                                            {opt.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />

                    {/* Subscription Status */}
                    <Controller
                        name="subscriptionStatus"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                            <FormControl variant="standard" fullWidth>
                                <InputLabel>Subscription status</InputLabel>
                                <Select
                                    {...field}
                                    value={field.value ?? ""}
                                    onChange={(e) => {
                                        field.onChange(e.target.value);
                                        handleDynamicSubmitForm(e);
                                    }}
                                >
                                    <MenuItem value="">No selection</MenuItem>
                                    {SUBSCRIPTION_STATUS_OPTIONS.map((opt) => (
                                        <MenuItem
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />

                    {/* Subscription Source */}
                    <Controller
                        name="subscriptionSource"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                            <FormControl variant="standard" fullWidth>
                                <InputLabel>Subscription source</InputLabel>
                                <Select
                                    {...field}
                                    value={field.value ?? ""}
                                    onChange={(e) => {
                                        field.onChange(e.target.value);
                                        handleDynamicSubmitForm(e);
                                    }}
                                >
                                    <MenuItem value="">No selection</MenuItem>
                                    {sourceOptions.map((opt) => (
                                        <MenuItem
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                    />

                    <Controller
                        name="renewable"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                            <FormControlLabel
                                label="Renewable only"
                                control={
                                    <Checkbox
                                        checked={field.value}
                                        onChange={(e) => {
                                            field.onChange(e.target.checked);
                                            handleDynamicSubmitForm(e);
                                        }}
                                    />
                                }
                            />
                        )}
                    />
                </Stack>
            </Stack>
        </form>
    );
};

export default CompaniesFilters;
